import cloneDeep from 'lodash.clonedeep';

import { getEncodedVariantId } from './VariantUtils';
import { getEncodedProductId, getDecodedId } from './ProductUtils';

/**
 * Get Steps Data
 *
 * (Wish gsData was an array and not an object)
 * 1. Create empty array allSteps that will be returned
 * 2. Loop through gsSteps object
 *    1. Create empty step object
 *    2. Add title to step object
 *    3. Create empty stepProducts array which we will add products to
 *    4. Loop through gsSteps Products object
 *       1. Get stepProduct from products array where gsStepProduct id matches products id
 *       2. Add Extra product Data to stepProduct based on what Growth Spark provided.
 *       3. If  addProductData returns the product, add it to the stepProducts array
 *    5. Add stepProducts Array to to step object
 *    6. Add step object to allSteps Array
 *
 * @param {Object} gsSteps - growth spark custom data steps object provided by customizable collections app
 * @param {Array} products - Array of collection products
 * @return {Array} allSteps - Return allSteps array with all the product data included
 */
export var getStepsData = function getStepsData(gsSteps, products) {
  var allSteps = [];
  Object.values(gsSteps).map(function (gsStep) {
    var step = {};
    step.title = gsStep.title;
    step.description = gsStep.description;
    var gsStepProducts = gsStep.products;
    var stepProducts = [];
    Object.values(gsStepProducts).map(function (gsStepProduct) {
      var stepProduct = getStepProduct(products, gsStepProduct.product_id);
      var stepProductWithData = stepProduct && addProductData(stepProduct, gsStepProduct);
      if (stepProductWithData) {
        stepProducts.push(stepProductWithData);
      }
    });

    step.products = stepProducts;
    allSteps.push(step);
  });

  return allSteps;
};

/**
 * Add Product Data
 *
 * Basically remove unused variants and add default variant and featured attributes
 * 1. Make a deep copy of product. (Possible to have two of the same product
 *    and removing a lot of the data to cater to Dormify needs)
 * 2. Filter any variants that are not in the gsProduct variants array
 *    a. Also add the filtered variants selected options to the stepProductOptions array (used in step 3)
 *    b. If the variant is the gsProduct default variant , add selected_variant = true
 * 3. Next loop through stepProduct options and remove any options that are not in the
 *    stepProductOptions array. (this will remove unused options from Collection Card)
 * 4. If stepProductVariants are found,
 *    a. Add them to stepProduct.node.variants.edges
 *    b. Add default_variant to stepProduct node
 *    c. Add featured attribute if step product is featured (Adds the star icon to image)
 *    d. Return the newly created stepProduct
 *
 * @param {Object} product - Product Object
 * @param {Object} gsProduct - Product Object Provided by GrowthSpark
 * @return {object} stepProduct - Product Object with extra data
 */
export var addProductData = function addProductData(product, gsProduct) {
  var stepProduct = cloneDeep(product);
  var stepProductOptions = [];
  var stepProductVariants = stepProduct.node.variants.edges.filter(function (variant) {
    if (gsProduct.variants.includes(variant.node.variant_id)) {
      variant.node.selectedOptions.map(function (option) {
        stepProductOptions.push(option.value);
      });
      if (gsProduct.default_variant === variant.node.variant_id) {
        variant.node.default_variant = true;
      }
      return variant;
    }
  });
  stepProduct.node.options.map(function (option, i) {
    var newValues = option.values.filter(function (value) {
      if (stepProductOptions.includes(value)) {
        return value;
      }
    });
    stepProduct.node.options[i].values = newValues;
  });
  if (stepProductVariants.length > 0) {
    stepProduct.node.variants.edges = stepProductVariants;
    stepProduct.node.default_variant = gsProduct.default_variant;
    stepProduct.node.featured = gsProduct.featured;
    return stepProduct;
  }
};

/**
 * Get Step Product
 *
 * Using gsProductId, return the product that matches given ID
 *
 * @param {Array} products - Array of collection products
 * @param {String} gsProductId - Product ID provided by Growth Spark
 * @return {object} stepProduct - The product that matches gsProductId
 */
export var getStepProduct = function getStepProduct(products, gsProductId) {
  return products.find(function (x) {
    return x.node.product_id === gsProductId;
  });
};

/**
 * Add Product and Variant Ids
 *
 * Since storefront API doesn't provide shopify ids,
 * We need to decode all the ids
 *  and add as a new product_id, variant_id
 *
 * @param {Array} products - Array of collection products
 * @return {Array} same array with product_id and variant_ids added
 */
export var addProductAndVariantIds = function addProductAndVariantIds(products) {
  return products.map(function (product) {
    product.node.product_id = getDecodedId(product.node.id);

    product.node.variants.edges.map(function (variant) {
      variant.node.variant_id = getDecodedId(variant.node.id);
    });
    return product;
  });
};

/**
 * Add Necessary Attributes To Dy Items
 *
 * DY API doesn't provide us with all necessary items to
 * add product variants to cart. so after we receive
 * the items array, we loop through and add the needed
 * items with this function
 *
 * @param {array} items - Array of DY items (products)
 * @return {array} Same array with added attributes
 */
export var addNecessaryAttributesToDyItems = function addNecessaryAttributesToDyItems(items) {
  items.map(function (item) {
    var product = item.item;
    product.vendor = product.brand;
    product.id = getEncodedProductId(product.group_id);
    var hackyHandle = product.url.split('/').pop();
    product.handle = hackyHandle.split('?')[0];
    product.selectedVariantId = getEncodedVariantId(product.sku);
  });
  return items;
};
/**
 * Filter Out DY Size Variants
 *
 * @param {array} items - Array of DY items (products)
 * @param {string} sizeOption - Size option of added product
 * @return {array} Filtered Array
 */
export var filterOutDySizeVariants = function filterOutDySizeVariants(items, sizeOption) {
  return items.filter(function (item) {
    return item.item.size === '' || item.item.size.includes(sizeOption) || sizeOption.includes(item.item.size);
  });
};

/**
 * Create Custom Collection Gallery Arrays
 *
 * @param {array} handleArr - Array of product handles (strings)
 * @param {array} prodArr - Array of products in collection
 */

export var createCustomCollectionGalleryArrays = function createCustomCollectionGalleryArrays(stepObj, prodArr) {
  var productArr = prodArr.filter(function (prod) {
    return stepObj.products.some(function (handle) {
      return handle === prod.node.handle;
    });
  });

  return { products: productArr, title: stepObj.title };
};